import { DeleteOutlined, EditOutlined, EyeOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Pagination, ProductTableRow, ProductTableData } from '@app/models/products/ProductResponse';
import { getProductTableData } from '@app/api/products.api';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { useMounted } from '@app/hooks/useMounted';
import { Space, Tooltip, Button, Popconfirm, Input } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { notificationController } from '@app/controllers/notificationController';
import FilterComponent from '@app/components/TableFilter/TableFilter';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
};

export const ProductsTable: React.FC = () => {
  const [tableData, setTableData] = useState<{ data: ProductTableRow[]; pagination: Pagination; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });

  const { t } = useTranslation();
  const { isMounted } = useMounted();
  const navigate = useNavigate();
  const [statusOptions, setStatusOptions] = useState<Record<string, { label: string; value: string }[]> | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<{ label: string; value: string }[] | null>(null);
  const [filters, setFilters] = useState<{ [key: string]: string | [string, string] | null }>({});
  const [form] = BaseButtonsForm.useForm();
  const lastFetchedRef = useRef(false);

  const fetch = useCallback(
    (pagination: Pagination) => {
      setTableData((tableData) => ({ ...tableData, loading: true }));
      getProductTableData({ ...pagination, ...filters })
        .then((res) => {
          if (isMounted.current) {
            setTableData({
              data: res.data,
              pagination: {
                current: res.pagination.current,
                pageSize: res.pagination.pageSize,
                total: res.pagination.total,
              },
              loading: false,
            });
            setStatusOptions(res.status_options);
            setCategoryOptions(
              res.product_categories.map((category: { id: number; name: string }) => ({
                label: category.name,
                value: category.id.toString(),
              })),
            );
          }
        })
        .catch((error: any) => {
          notificationController.error({
            message: error.message,
          });
        });
    },
    [isMounted, filters],
  );

  useEffect(() => {
    if (!lastFetchedRef.current) {
      lastFetchedRef.current = true;
      fetch(initialPagination);
    }
  }, [fetch]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    fetch({
      ...filters,
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 5,
    });
  };

  const handleViewRow = (id: number) => {
    navigate(`/view-product`, { state: { id } });
  };

  const handleEditRow = (id: number) => {
    navigate(`/edit-product`, { state: { id } });
  };

  const handleDeleteRow = (id: number) => {
    console.log(`Deleting row with id`, { state: { id } });
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span
        style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}
      >{`Showing ${range[0]} - ${range[1]} of ${total} Products`}</span>
    );
  };

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown';
    options?: Record<string, { label: string; value: string }[]>;
  }[] = [
    { name: 'search', placeholder: 'Search', type: 'text' },
    {
      name: 'status',
      placeholder: 'Status',
      type: 'dropdown',
      options: statusOptions || { statusOptions: [] },
    },
    {
      name: 'product_category',
      placeholder: 'Product Category',
      type: 'dropdown',
      options: categoryOptions ? { categoryOptions: categoryOptions } : {},
    },
  ];

  const columns: ColumnsType<ProductTableRow> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: '30%',
    },
    {
      title: 'Product Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Product Category',
      key: 'product_category',
      render: (_, record) => record.product_category.name,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '30%',
      render: (_, record) => {
        return (
          <Space>
            <Tooltip title="View">
              <Button
                type="text"
                onClick={() => handleViewRow(record.id)}
                icon={<EyeOutlined />}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>

            <Tooltip title="Edit">
              <Button
                type="text"
                onClick={() => handleEditRow(record.id)}
                icon={<EditOutlined />}
                style={{ color: '#52c41a' }}
              />
            </Tooltip>

            <Tooltip title="Delete">
              <Popconfirm
                title="Are you sure to delete this role?"
                onConfirm={() => handleDeleteRow(record.id)}
                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              >
                <Button type="text" danger icon={<DeleteOutlined />} style={{ color: 'red' }} />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <BaseCard id="basic-table" title="Products" padding="1.25rem">
      <FilterComponent data={searchData} onFilter={setFilters} />
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </BaseCard>
  );
};
